import type { CSSProperties } from 'react';
import './loading.css';

/** Decorative only. Pair a group of placeholders with an accessible LoadingIndicator. */
export function Skeleton({
  width,
  className = '',
}: {
  width?: string;
  className?: string;
}): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`ui-skeleton ${className}`.trim()}
      style={width ? { width } : undefined}
    />
  );
}

export function LoadingIndicator({
  label,
  className = '',
}: {
  label: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`ui-loading-indicator ${className}`.trim()} role="status" aria-atomic="true">
      <span className="ui-loading-indicator__ring ui-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** Use only while there is no data. Keep existing content visible during background refresh. */
export function LoadingState({
  label,
  kind = 'rows',
  count = 3,
  className = '',
}: {
  label: string;
  kind?: 'history' | 'rows' | 'cards' | 'form' | 'summary';
  count?: number;
  className?: string;
}): JSX.Element {
  return (
    <div className={`ui-loading ui-loading--${kind} ${className}`.trim()}>
      <LoadingIndicator label={label} />
      <div className="ui-loading__content" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <div
            key={index}
            className="ui-loading__item"
            style={{ '--loading-delay': `${index * 90}ms` } as CSSProperties}
          >
            {(kind === 'cards' || kind === 'rows') && (
              <Skeleton className={kind === 'cards' ? 'ui-skeleton--cover' : 'ui-skeleton--icon'} />
            )}
            <div className="ui-loading__lines">
              <Skeleton
                width={kind === 'history' ? '42%' : kind === 'form' ? '25%' : '64%'}
                className="ui-skeleton--heading"
              />
              {kind !== 'form' && <Skeleton width={index % 2 ? '46%' : '56%'} />}
              {(kind === 'history' || kind === 'cards' || kind === 'summary') && (
                <Skeleton width="32%" className="ui-skeleton--detail" />
              )}
              {kind === 'form' && <Skeleton className="ui-skeleton--input" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
